import { useCallback, useEffect, useState } from "react";
import { ExternalLink, Link2, RefreshCw, Trash2 } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { apiFetch } from "./api";

const buildLink = (inputs) => {
    const q = new URLSearchParams({
        name: inputs.dealerName || "",
        d: inputs.deals,
        pen: inputs.penetration,
        sr: inputs.salesRate,
        ros: inputs.totalRos,
        cp: inputs.cpRos,
        svr: inputs.serviceRate,
        ret: inputs.retention,
    });
    return `${window.location.origin}/proforma?${q.toString()}`;
};

export const Proformas = ({ token, onAuthError }) => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            setItems(await apiFetch("/proforma-scenarios", { token }));
        } catch (err) {
            if (err.status === 401) return onAuthError();
            toast.error("Could not load scenarios", { description: err.message });
        } finally {
            setLoading(false);
        }
    }, [token, onAuthError]);

    useEffect(() => {
        load();
    }, [load]);

    const remove = async (id) => {
        try {
            await apiFetch(`/proforma-scenarios/${id}`, { token, method: "DELETE" });
            setItems((xs) => xs.filter((x) => x.id !== id));
        } catch (err) {
            if (err.status === 401) return onAuthError();
            toast.error("Delete failed", { description: err.message });
        }
    };

    const copy = async (inputs) => {
        try {
            await navigator.clipboard.writeText(buildLink(inputs));
            toast.success("Share link copied");
        } catch {
            toast.error("Copy failed");
        }
    };

    return (
        <div data-testid="admin-proformas-tab">
            <div className="flex items-center justify-between">
                <p className="text-sm text-slate-400">
                    {items.length} saved {items.length === 1 ? "scenario" : "scenarios"} —
                    save new ones from the /proforma calculator.
                </p>
                <button
                    data-testid="proformas-refresh-btn"
                    onClick={load}
                    className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-xs font-semibold text-white transition-colors hover:border-sky-400/50"
                >
                    <RefreshCw className="h-3.5 w-3.5" /> Refresh
                </button>
            </div>

            <div className="mt-5 flex flex-col gap-3">
                {loading ? (
                    <p className="py-10 text-center text-sm text-slate-500">Loading…</p>
                ) : items.length === 0 ? (
                    <p className="rounded-2xl border border-white/10 bg-[#0C1019] py-10 text-center text-sm text-slate-500">
                        No saved scenarios yet. Run the calculator at /proforma and hit
                        "Save scenario".
                    </p>
                ) : (
                    items.map((s) => (
                        <div
                            key={s.id}
                            data-testid={`proforma-scenario-${s.id}`}
                            className="flex flex-col gap-4 rounded-2xl border border-white/10 bg-[#0C1019] p-5 md:flex-row md:items-center md:justify-between"
                        >
                            <div>
                                <p className="font-display text-base font-semibold text-white">
                                    {s.name || "Untitled scenario"}
                                </p>
                                <p className="mt-1 text-xs text-slate-500">
                                    {new Date(s.created_at).toLocaleString()} ·{" "}
                                    {s.inputs.deals} deals · {s.inputs.penetration}% pen ·{" "}
                                    {s.inputs.salesRate}% retail conv · {s.inputs.cpRos}{" "}
                                    CP ROs · {s.inputs.serviceRate}% service conv ·{" "}
                                    {s.inputs.retention} mo retention ·{" "}
                                    {s.inputs.earlyCancel ?? 10}% early cancels
                                </p>
                            </div>
                            <div className="flex shrink-0 gap-2">
                                <button
                                    data-testid={`scenario-copy-${s.id}`}
                                    onClick={() => copy(s.inputs)}
                                    className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-xs font-semibold text-white transition-colors hover:border-sky-400/50"
                                >
                                    <Link2 className="h-3.5 w-3.5" /> Copy link
                                </button>
                                <a
                                    data-testid={`scenario-open-${s.id}`}
                                    href={buildLink(s.inputs)}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="inline-flex items-center gap-2 rounded-full bg-sky-400 px-4 py-2 text-xs font-bold text-[#0A0D14] transition-colors hover:bg-sky-300"
                                >
                                    <ExternalLink className="h-3.5 w-3.5" /> Open
                                </a>
                                <button
                                    data-testid={`scenario-delete-${s.id}`}
                                    onClick={() => remove(s.id)}
                                    className="rounded-lg p-2 text-slate-500 transition-colors hover:bg-red-500/10 hover:text-red-300"
                                    aria-label="Delete scenario"
                                >
                                    <Trash2 className="h-4 w-4" />
                                </button>
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};
